/**
 * DevQRTestPanel - QR code testing utility for development/testing
 *
 * Allows testing the catcher flow without physical QR scanners by:
 * 1. Quick Pick - 3 most common test codes for fast access
 * 2. Pre-generated golden test QR codes from documentation
 * 3. Fetching real garment units from the database
 * 4. Copy-to-clipboard for pasting into scanner input
 * 5. Custom QR code generation
 *
 * Design considerations for production floor use:
 * - Large touch targets (44px minimum for gloved hands)
 * - Responsive tab layout (2 cols mobile, 4 cols desktop)
 * - Copy success feedback on buttons
 * - Accessible with proper ARIA attributes
 *
 * Visibility controlled by production settings (Settings > Show Dev Tools)
 */
import { useState, useCallback } from 'react';
import { skipToken } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { useLocation } from '@/contexts/LocationContext';
import { trpc } from '@/lib/trpc';
import { encodeGarmentQR, createQRPayload } from '@/features/production-orders/utils/qr-encoder';
import { useProductionSettings } from '@/features/production-settings/hooks/useProductionSettings';
import {
  Copy,
  Check,
  Beaker,
  Database,
  Wand2,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  FileText,
  Zap,
} from 'lucide-react';

/**
 * Golden Test QR Codes from Testing-Golden-Batch.md
 * These use non-UUID IDs and require seeded test data to work with the backend.
 * Useful for copy-paste testing when test data is seeded.
 */
const GOLDEN_TEST_CODES = {
  singleLine: [
    {
      name: 'TEST-001 (POD, no pretreat)',
      description: 'White tee, front only → packing shelf',
      code: 'THP://eyJ2IjoxLCJnIjoiZ3VfdGVzdF8wMDEiLCJvIjoiVEVTVC0wMDEiLCJzIjoiVFNISVJULVdIVC1NIiwibSI6IkRURyIsInQiOiJQT0QifQ==',
    },
    {
      name: 'TEST-002 (POD, pretreat)',
      description: 'Black tee, needs pretreat',
      code: 'THP://eyJ2IjoxLCJnIjoiZ3VfdGVzdF8wMDIiLCJvIjoiVEVTVC0wMDIiLCJzIjoiVFNISVJULUJMSy1MIiwibSI6IkRURyIsInQiOiJQT0QifQ==',
    },
    {
      name: 'TEST-003 (POD, dual print)',
      description: 'Navy XL, front + back',
      code: 'THP://eyJ2IjoxLCJnIjoiZ3VfdGVzdF8wMDMiLCJvIjoiVEVTVC0wMDMiLCJzIjoiVFNISVJULU5BVi1YTCIsIm0iOiJEVEciLCJ0IjoiUE9EIn0=',
    },
    {
      name: 'TEST-004 (P&P)',
      description: 'Pick & pack, no printing',
      code: 'THP://eyJ2IjoxLCJnIjoiZ3VfdGVzdF8wMDQiLCJvIjoiVEVTVC0wMDQiLCJzIjoiU09DS1MtQkxLLU9ORSIsIm0iOiJQUCIsInQiOiJQUCJ9',
    },
  ],
  multiLine: [
    {
      name: 'TEST-005a (multi POD)',
      description: '3-item order, white tee',
      code: 'THP://eyJ2IjoxLCJnIjoiZ3VfdGVzdF8wMDVhIiwibyI6IlRFU1QtMDA1IiwicyI6IlRTSElSVC1XSFQtTSIsIm0iOiJEVEciLCJ0IjoiUE9EIn0=',
    },
    {
      name: 'TEST-005b (multi POD)',
      description: '3-item order, black tee',
      code: 'THP://eyJ2IjoxLCJnIjoiZ3VfdGVzdF8wMDViIiwibyI6IlRFU1QtMDA1IiwicyI6IlRTSElSVC1CTEstTSIsIm0iOiJEVEciLCJ0IjoiUE9EIn0=',
    },
    {
      name: 'TEST-005c (multi POD)',
      description: '3-item order, grey hoodie',
      code: 'THP://eyJ2IjoxLCJnIjoiZ3VfdGVzdF8wMDVjIiwibyI6IlRFU1QtMDA1IiwicyI6IkhPT0RJRS1HUlktTCIsIm0iOiJEVEciLCJ0IjoiUE9EIn0=',
    },
    {
      name: 'TEST-006a (multi P&P)',
      description: '2-item P&P order',
      code: 'THP://eyJ2IjoxLCJnIjoiZ3VfdGVzdF8wMDZhIiwibyI6IlRFU1QtMDA2IiwicyI6IlNPQ0tTLUJMSy1PTkUiLCJtIjoiUFAiLCJ0IjoiUFAifQ==',
    },
    {
      name: 'TEST-007a (mixed POD)',
      description: '4-item mixed, POD item',
      code: 'THP://eyJ2IjoxLCJnIjoiZ3VfdGVzdF8wMDdhIiwibyI6IlRFU1QtMDA3IiwicyI6IlRTSElSVC1CTEstTCIsIm0iOiJEVEciLCJ0IjoiUE9EIn0=',
    },
    {
      name: 'TEST-007c (mixed P&P)',
      description: '4-item mixed, P&P item',
      code: 'THP://eyJ2IjoxLCJnIjoiZ3VfdGVzdF8wMDdjIiwibyI6IlRFU1QtMDA3IiwicyI6IlNPQ0tTLUJMSy1PTkUiLCJtIjoiUFAiLCJ0IjoiUFAifQ==',
    },
  ],
  redo: [
    {
      name: 'TEST-008 (redo)',
      description: 'Redo item from failed print',
      code: 'THP://eyJ2IjoxLCJnIjoiZ3VfdGVzdF8wMDhfcmVkbyIsIm8iOiJURVNULTAwNyIsInMiOiJUU0hJUlQtQkxLLUwiLCJtIjoiRFRHIiwidCI6IlBPRCJ9',
    },
  ],
  stress: [
    {
      name: 'TEST-009a (stress)',
      description: '5-item stress test, item 1',
      code: 'THP://eyJ2IjoxLCJnIjoiZ3VfdGVzdF8wMDlhIiwibyI6IlRFU1QtMDA5IiwicyI6IlRTSElSVC1CTEstUyIsIm0iOiJEVEciLCJ0IjoiUE9EIn0=',
    },
    {
      name: 'TEST-009b (stress)',
      description: '5-item stress test, item 2',
      code: 'THP://eyJ2IjoxLCJnIjoiZ3VfdGVzdF8wMDliIiwibyI6IlRFU1QtMDA5IiwicyI6IlRTSElSVC1CTEstTSIsIm0iOiJEVEciLCJ0IjoiUE9EIn0=',
    },
  ],
};

// Quick pick - 3 most common test scenarios for fast access
const QUICK_PICK_CODES: Array<{ name: string; description: string; code: string }> = [
  GOLDEN_TEST_CODES.singleLine[0]!, // Single POD
  GOLDEN_TEST_CODES.singleLine[1]!, // Dark garment (pretreat)
  GOLDEN_TEST_CODES.multiLine[0]!,  // Multi-line first item
];

// Static infrastructure QR codes
const STATIC_QR_CODES = [
  { name: 'BIN-TEST-01', code: 'THP://BIN:BIN-TEST-01', description: 'Test bin 1' },
  { name: 'BIN-TEST-02', code: 'THP://BIN:BIN-TEST-02', description: 'Test bin 2' },
  { name: 'BIN-TEST-03', code: 'THP://BIN:BIN-TEST-03', description: 'Test bin 3' },
  { name: 'BIN-TEST-04', code: 'THP://BIN:BIN-TEST-04', description: 'Test bin 4' },
  { name: 'Packing Shelf', code: 'THP://SHELF:PACKING', description: 'Single-line destination' },
  { name: 'OOS Bin', code: 'THP://BIN:OOS-001', description: 'Out of stock bin' },
];

// Test scenarios with valid UUIDs (for when no seeded data)
const UUID_TEST_SCENARIOS = [
  {
    name: 'Single-line POD (no pretreat)',
    description: 'Valid UUID - will fail lookup without matching DB record',
    payload: createQRPayload('aaaaaaaa-1111-4000-8000-000000000001', 'TEST-001', 'TSHIRT-WHT-M', 'POD'),
  },
  {
    name: 'Single-line POD (dark)',
    description: 'Valid UUID - will fail lookup without matching DB record',
    payload: createQRPayload('aaaaaaaa-2222-4000-8000-000000000002', 'TEST-002', 'TSHIRT-BLK-L', 'POD'),
  },
  {
    name: 'P&P item',
    description: 'Valid UUID - will fail lookup without matching DB record',
    payload: createQRPayload('aaaaaaaa-4444-4000-8000-000000000004', 'TEST-004', 'CAP-RED-OS', 'P&P'),
  },
];

interface QRItemProps {
  label: string;
  description?: string;
  qrCode: string;
  onCopy: (code: string) => void;
  copiedCode: string | null;
  compact?: boolean;
}

function QRItem({ label, description, qrCode, onCopy, copiedCode, compact = false }: QRItemProps) {
  const isCopied = copiedCode === qrCode;

  return (
    <div className={`flex items-center justify-between ${compact ? 'p-2' : 'p-3'} bg-muted/50 rounded-lg`}>
      <div className="flex-1 min-w-0">
        <div className={`font-medium ${compact ? 'text-xs' : 'text-sm'}`}>{label}</div>
        {description && !compact && (
          <div className="text-xs text-muted-foreground/90 truncate">{description}</div>
        )}
        {!compact && (
          <code className="text-xs text-muted-foreground block truncate mt-1">
            {qrCode.length > 40 ? `${qrCode.slice(0, 40)}…` : qrCode}
          </code>
        )}
      </div>
      <Button
        variant={isCopied ? 'default' : 'outline'}
        size={compact ? 'sm' : 'default'}
        className={`ml-2 shrink-0 ${compact ? 'h-8 w-8 p-0' : 'h-10 min-w-[44px]'}`}
        onClick={() => onCopy(qrCode)}
        aria-label={`Copy ${label} QR code`}
      >
        {isCopied ? (
          <Check className={compact ? 'h-3 w-3' : 'h-4 w-4'} />
        ) : (
          <Copy className={compact ? 'h-3 w-3' : 'h-4 w-4'} />
        )}
      </Button>
    </div>
  );
}

interface DevQRTestPanelProps {
  /**
   * standalone: Full card with collapse (default, backward compatible)
   * embedded: Content only, for use inside DevToolsPanel
   */
  mode?: 'standalone' | 'embedded';
}

export function DevQRTestPanel({ mode = 'standalone' }: DevQRTestPanelProps) {
  const { toast } = useToast();
  const { selectedLocation } = useLocation();
  const { settings } = useProductionSettings();
  const [isExpanded, setIsExpanded] = useState(false);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [customId, setCustomId] = useState('');
  const [customOrder, setCustomOrder] = useState('TEST-CUSTOM');
  const [customSku, setCustomSku] = useState('TEST-SKU-001');

  // In embedded mode, always fetch (parent controls visibility)
  // In standalone mode, only fetch when expanded
  const shouldFetch = mode === 'embedded' || isExpanded;

  // Fetch real garment units from database via tRPC
  const {
    data: realGarments,
    isLoading: isLoadingGarments,
    refetch: refetchGarments,
  } = trpc.catcher.listTestableGarments.useQuery(
    selectedLocation?.id ? { locationId: selectedLocation.id, limit: 20 } : skipToken,
    { enabled: shouldFetch && !!selectedLocation?.id }
  );

  const handleCopy = useCallback((code: string) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopiedCode(code);
      toast({
        title: 'Copied!',
        description: 'QR code copied to clipboard. Paste into scanner input.',
        duration: 2000,
      });
      // Reset copied state after 3 seconds
      setTimeout(() => setCopiedCode(null), 3000);
    }).catch(() => {
      toast({
        variant: 'destructive',
        title: 'Copy failed',
        description: 'Could not copy to clipboard',
      });
    });
  }, [toast]);

  const handleGenerateCustom = useCallback(() => {
    if (!customId) {
      toast({
        variant: 'destructive',
        title: 'Missing ID',
        description: 'Enter a garment unit ID (UUID format)',
      });
      return;
    }

    // Validate UUID format
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(customId)) {
      toast({
        variant: 'destructive',
        title: 'Invalid ID format',
        description: 'ID must be a valid UUID (e.g., 123e4567-e89b-12d3-a456-426614174000)',
      });
      return;
    }

    try {
      const payload = createQRPayload(customId, customOrder, customSku, 'POD');
      const qrCode = encodeGarmentQR(payload);
      handleCopy(qrCode);
    } catch {
      toast({
        variant: 'destructive',
        title: 'Generation failed',
        description: 'Could not generate QR code',
      });
    }
  }, [customId, customOrder, customSku, handleCopy, toast]);

  // In embedded mode, parent handles visibility
  // In standalone mode, hide if dev tools are disabled in settings
  if (mode === 'standalone' && !settings.showDevTools) {
    return null;
  }

  // Content that's shared between modes
  const panelContent = (
    <>
      {/* Quick Pick Row - Always visible for fast access */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          <Zap className="h-3 w-3" aria-hidden="true" />
          Quick Pick
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {QUICK_PICK_CODES.map((item, i) => (
            <QRItem
              key={i}
              label={item.name}
              qrCode={item.code}
              onCopy={handleCopy}
              copiedCode={copiedCode}
              compact
            />
          ))}
        </div>
      </div>

      <Tabs defaultValue="golden" className="w-full">
        {/* Responsive tabs: 2 columns on mobile, 4 on desktop */}
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 h-auto gap-1 p-1">
          <TabsTrigger value="golden" className="text-xs sm:text-sm py-2.5 gap-1.5">
            <FileText className="h-4 w-4" aria-hidden="true" />
            <span className="hidden xs:inline">Golden</span>
            <span className="xs:hidden">Gold</span>
          </TabsTrigger>
          <TabsTrigger value="database" className="text-xs sm:text-sm py-2.5 gap-1.5">
            <Database className="h-4 w-4" aria-hidden="true" />
            <span>DB</span>
          </TabsTrigger>
          <TabsTrigger value="static" className="text-xs sm:text-sm py-2.5 gap-1.5">
            <Wand2 className="h-4 w-4" aria-hidden="true" />
            <span>Bins</span>
          </TabsTrigger>
          <TabsTrigger value="custom" className="text-xs sm:text-sm py-2.5 gap-1.5">
            <Beaker className="h-4 w-4" aria-hidden="true" />
            <span>Custom</span>
          </TabsTrigger>
        </TabsList>

        {/* Golden Test Codes from Documentation */}
        <TabsContent value="golden" className="mt-3 space-y-4">
          <p className="text-xs text-muted-foreground/90">
            Pre-generated QR codes from Testing-Golden-Batch.md.
            <strong className="text-yellow-600"> Requires seeded test data</strong> to work with backend.
          </p>

          <div className="space-y-4">
            <div>
              <h4 className="text-xs font-semibold mb-2 text-muted-foreground uppercase tracking-wide">
                Single-Line Orders
              </h4>
              <div className="space-y-2">
                {GOLDEN_TEST_CODES.singleLine.map((item, i) => (
                  <QRItem
                    key={i}
                    label={item.name}
                    description={item.description}
                    qrCode={item.code}
                    onCopy={handleCopy}
                    copiedCode={copiedCode}
                  />
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-xs font-semibold mb-2 text-muted-foreground uppercase tracking-wide">
                Multi-Line Orders
              </h4>
              <div className="space-y-2">
                {GOLDEN_TEST_CODES.multiLine.map((item, i) => (
                  <QRItem
                    key={i}
                    label={item.name}
                    description={item.description}
                    qrCode={item.code}
                    onCopy={handleCopy}
                    copiedCode={copiedCode}
                  />
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-xs font-semibold mb-2 text-muted-foreground uppercase tracking-wide">
                Redo & Stress Tests
              </h4>
              <div className="space-y-2">
                {[...GOLDEN_TEST_CODES.redo, ...GOLDEN_TEST_CODES.stress].map((item, i) => (
                  <QRItem
                    key={i}
                    label={item.name}
                    description={item.description}
                    qrCode={item.code}
                    onCopy={handleCopy}
                    copiedCode={copiedCode}
                  />
                ))}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Real Database Garments */}
        <TabsContent value="database" className="mt-3 space-y-2">
          {isLoadingGarments ? (
            <div className="space-y-2">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          ) : realGarments && realGarments.length > 0 ? (
            <>
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs text-muted-foreground/90">
                  Real garments from database ({realGarments.length} found)
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => refetchGarments()}
                  className="h-9 px-3 gap-2"
                  aria-label="Refresh garment list"
                >
                  <RefreshCw className="h-4 w-4" aria-hidden="true" />
                  <span className="hidden sm:inline">Refresh</span>
                </Button>
              </div>
              {realGarments.map((garment) => (
                <QRItem
                  key={garment.id}
                  label={`${garment.orderName} - ${garment.sku}`}
                  description={`${garment.status}${garment.isRedo ? ' (REDO)' : ''}${garment.batchLabel ? ` • ${garment.batchLabel}` : ''}`}
                  qrCode={encodeGarmentQR(createQRPayload(garment.id, garment.orderName, garment.sku, 'POD'))}
                  onCopy={handleCopy}
                  copiedCode={copiedCode}
                />
              ))}
            </>
          ) : (
            <>
              <p className="text-xs text-muted-foreground/90 mb-3">
                No garments with LABEL_PRINTED or PICKED status. Create a batch first!
              </p>
              <p className="text-xs text-muted-foreground/90 mb-3">
                Fallback: UUID-based test codes (will fail lookup but test QR parsing):
              </p>
              {UUID_TEST_SCENARIOS.map((scenario, i) => (
                <QRItem
                  key={i}
                  label={scenario.name}
                  description={scenario.description}
                  qrCode={encodeGarmentQR(scenario.payload)}
                  onCopy={handleCopy}
                  copiedCode={copiedCode}
                />
              ))}
            </>
          )}
        </TabsContent>

        {/* Static Infrastructure QR Codes */}
        <TabsContent value="static" className="mt-3 space-y-2">
          <p className="text-xs text-muted-foreground/90 mb-3">
            Bin and shelf QR codes. Format: THP://BIN:xxx or THP://SHELF:xxx
          </p>
          {STATIC_QR_CODES.map((item, i) => (
            <QRItem
              key={i}
              label={item.name}
              description={item.description}
              qrCode={item.code}
              onCopy={handleCopy}
              copiedCode={copiedCode}
            />
          ))}
        </TabsContent>

        {/* Custom QR Generator */}
        <TabsContent value="custom" className="mt-3 space-y-4">
          <p className="text-xs text-muted-foreground/90">
            Generate a QR code with a real garment unit ID from your database.
          </p>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="garment-id" className="text-sm font-medium">
                Garment Unit ID (UUID)
              </Label>
              <Input
                id="garment-id"
                placeholder="e.g., 123e4567-e89b-12d3-a456-426614174000"
                value={customId}
                onChange={(e) => setCustomId(e.target.value)}
                className="font-mono text-sm h-11"
                aria-describedby="garment-id-help"
              />
              <p id="garment-id-help" className="text-xs text-muted-foreground">
                Copy a real UUID from the database query below
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="order-num" className="text-sm font-medium">
                  Order Number
                </Label>
                <Input
                  id="order-num"
                  value={customOrder}
                  onChange={(e) => setCustomOrder(e.target.value)}
                  className="text-sm h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sku" className="text-sm font-medium">
                  SKU
                </Label>
                <Input
                  id="sku"
                  value={customSku}
                  onChange={(e) => setCustomSku(e.target.value)}
                  className="text-sm h-11"
                />
              </div>
            </div>

            <Button
              onClick={handleGenerateCustom}
              className="w-full h-12 text-base gap-2"
              size="lg"
            >
              <Copy className="h-4 w-4" aria-hidden="true" />
              Generate & Copy QR
            </Button>
          </div>

          <div className="pt-3 border-t">
            <p className="text-xs text-muted-foreground mb-2 font-medium">
              SQL to find testable garments:
            </p>
            <pre className="text-xs bg-muted p-3 rounded-lg overflow-x-auto font-mono">
{`SELECT id, sku, status
FROM prod_garment_units
WHERE status IN ('LABEL_PRINTED', 'PICKED')
LIMIT 10;`}
            </pre>
          </div>
        </TabsContent>
      </Tabs>
    </>
  );

  // Embedded mode: return content directly with padding
  if (mode === 'embedded') {
    return (
      <div className="p-4 space-y-4">
        {panelContent}
      </div>
    );
  }

  // Standalone mode: full card with collapse (existing behavior)
  return (
    <Card className="border-dashed border-2 border-yellow-500/50 bg-yellow-500/5">
      <CardHeader
        className="pb-2 cursor-pointer select-none"
        onClick={() => setIsExpanded(!isExpanded)}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        aria-controls="dev-qr-panel-content"
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsExpanded(!isExpanded);
          }
        }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Beaker className="h-5 w-5 text-yellow-600" aria-hidden="true" />
            <CardTitle className="text-base font-medium text-yellow-700 dark:text-yellow-500">
              QR Test Panel
            </CardTitle>
            <Badge variant="outline" className="text-xs border-yellow-500/50 text-yellow-700 dark:text-yellow-500">
              DEV
            </Badge>
          </div>
          <div className="p-1">
            {isExpanded ? (
              <ChevronUp className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
            ) : (
              <ChevronDown className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
            )}
          </div>
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent id="dev-qr-panel-content" className="pt-0 space-y-4">
          {panelContent}
        </CardContent>
      )}
    </Card>
  );
}
